import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import {
  ChevronDown,
  Keyboard,
  Languages,
  LogIn,
  LogOut,
  MessageSquarePlus,
  Mic,
  Palette,
  Settings,
  SlidersHorizontal,
  UserRound,
} from "lucide-react";

import { CampAvatar } from "@/components/camp/CampAvatar";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRoomShortcutsSupported } from "@/components/room/useRoomShortcutsSupported";
import { campIdentityFor } from "@/components/room/campIdentity";
import type { SettingsSection } from "@/components/room/DeviceSettingsPanel";
import {
  SettingsButton,
  type SettingsButtonProps,
} from "@/components/settings/SettingsButton";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useAvatarChoice } from "@/hooks/useAvatarChoice";
import { useDisplayName } from "@/hooks/useDisplayName";
import { useHydrated } from "@/hooks/useHydrated";
import { useLocale } from "@/hooks/useLocale";
import { useSettings } from "@/hooks/useSettings";
import { useTheme } from "@/hooks/useTheme";
import { isTheme, THEME_CHOICES } from "@/engine/support/palette";
import { PaletteSwatch } from "@/components/settings/ThemeMenuRow";
import { getSessionPeerId } from "@/engine/state/roomIdentity";
import { setLocale } from "@/engine/state/locale";
import { setSetting } from "@/engine/state/settings";
import { LOCALES } from "@/engine/support/locales";
import type { EntryIntent } from "@/engine/state/entryIntent";
import { HomeProfileDialog } from "./HomeProfileDialog";

/** The devices the settings modal reports and changes, as home holds them. */
type DeviceProps = Pick<
  SettingsButtonProps,
  | "selectedAudioInput"
  | "selectedVideoInput"
  | "onAudioInputChange"
  | "onVideoInputChange"
>;

interface HomeAccountMenuProps extends DeviceProps {
  /** Where a sign-in round trip comes back to, when the visitor was going somewhere. */
  intent?: EntryIntent | null;
}

const icon = "size-4 text-muted-foreground";

/** Who the menu belongs to, at its head: the name, and the address when signed in. */
function MenuHead({ name, email }: { name: string; email: string | null }) {
  const { t } = useTranslation();
  return (
    <DropdownMenuLabel className="font-normal">
      <span className="block truncate text-sm font-semibold">{name}</span>
      <span
        data-testid="account-menu-email"
        className="block truncate text-xs text-muted-foreground"
      >
        {email ?? t("account.guest")}
      </span>
    </DropdownMenuLabel>
  );
}

/**
 * The keyboard shortcuts and the sections of the settings modal — what the gear
 * beside the logo used to hold, one level down so the menu stays short.
 */
function SettingsSub({
  onOpen,
}: {
  onOpen: (section: SettingsSection) => void;
}) {
  const { t } = useTranslation();
  const { shortcutHints } = useSettings();
  const shortcutsSupported = useRoomShortcutsSupported();
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger data-testid="account-menu-settings">
        <Settings className={icon} aria-hidden="true" />
        {t("settings.title")}
      </DropdownMenuSubTrigger>
      {/* Out of the menu, whose scrolling would clip it. */}
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          data-testid="account-menu-settings-list"
          className="w-60"
        >
          {/* A phone has no keyboard for the hints to describe (#797). */}
          {shortcutsSupported && (
            <DropdownMenuCheckboxItem
              data-testid="account-menu-shortcuts"
              checked={shortcutHints}
              onCheckedChange={(on) => setSetting("shortcutHints", on)}
            >
              <Keyboard className={icon} aria-hidden="true" />
              {t("settings.shortcutHints")}
            </DropdownMenuCheckboxItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="account-menu-application"
            onSelect={() => onOpen("application")}
          >
            <SlidersHorizontal className={icon} aria-hidden="true" />
            {t("settings.application")}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="account-menu-devices"
            onSelect={() => onOpen("devices")}
          >
            <Mic className={icon} aria-hidden="true" />
            {t("settings.devices")}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="account-menu-contact"
            onSelect={() => onOpen("contact")}
          >
            <MessageSquarePlus className={icon} aria-hidden="true" />
            {t("settings.contact")}
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}

/**
 * The palette, as radio rows with a swatch each, beside the language and at
 * the same depth: one level of submenu, so the pointer never has to cross a
 * second one on its way to a row.
 */
function ThemeSub() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger data-testid="account-menu-theme">
        <Palette className={icon} aria-hidden="true" />
        <span className="flex-1">{t("settings.theme")}</span>
        <span className="text-xs text-muted-foreground">
          {t(`settings.palette.${theme}`)}
        </span>
      </DropdownMenuSubTrigger>
      {/* Out of the menu, whose scrolling would clip it. */}
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          data-testid="account-menu-themes"
          aria-label={t("settings.chooseTheme")}
        >
          <DropdownMenuRadioGroup
            value={theme}
            onValueChange={(next) => {
              if (isTheme(next)) setTheme(next);
            }}
          >
            {THEME_CHOICES.map((choice) => (
              <DropdownMenuRadioItem
                key={choice}
                value={choice}
                data-testid={`account-menu-theme-${choice}`}
                className="gap-2"
              >
                <PaletteSwatch theme={choice} />
                {t(`settings.palette.${choice}`)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}

/**
 * The language, as a list of radio rows: ten fit in a submenu, and each is
 * named in its own language because that is the word its reader looks for.
 */
function LanguageSub() {
  const { t } = useTranslation();
  const locale = useLocale();
  const current = LOCALES.find((option) => option.code === locale);
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger data-testid="account-menu-language">
        <Languages className={icon} aria-hidden="true" />
        <span className="flex-1">{t("settings.language")}</span>
        <span className="text-xs text-muted-foreground">
          {current?.nativeName}
        </span>
      </DropdownMenuSubTrigger>
      {/* Out of the menu, whose scrolling would clip it. */}
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          data-testid="account-menu-languages"
          aria-label={t("settings.chooseLanguage")}
        >
          <DropdownMenuRadioGroup value={locale} onValueChange={setLocale}>
            {LOCALES.map((option) => (
              <DropdownMenuRadioItem
                key={option.code}
                value={option.code}
                lang={option.code}
                dir={option.direction}
              >
                {option.nativeName}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}

/** The way out for an account, the way in for a guest, nothing until known. */
function AccountFoot({
  ready,
  signedIn,
  intent,
  onSignOut,
}: {
  ready: boolean;
  signedIn: boolean;
  intent: EntryIntent | null;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();
  if (!ready) return null;
  return (
    <>
      <DropdownMenuSeparator />
      {signedIn ? (
        <DropdownMenuItem data-testid="account-sign-out" onSelect={onSignOut}>
          <LogOut className={icon} aria-hidden="true" />
          {t("account.signOut")}
        </DropdownMenuItem>
      ) : (
        <DropdownMenuItem asChild>
          {/* Signing in comes back to the room the visitor was heading for. */}
          <Link
            to="/auth"
            search={{ enter: intent?.kind, room: intent?.code }}
            data-testid="account-sign-up"
            aria-label={t("account.signUpLabel")}
          >
            <LogIn className={icon} aria-hidden="true" />
            {t("account.signUp")}
          </Link>
        </DropdownMenuItem>
      )}
    </>
  );
}

/**
 * Home's one corner for everything about the visitor (#821): who they are,
 * their character, how the app behaves, the language and the account. It used
 * to be three affordances — a gear by the logo, an avatar and an Account
 * button — two of which said the same thing.
 */
export function HomeAccountMenu({
  intent = null,
  ...devices
}: HomeAccountMenuProps) {
  const { t } = useTranslation();
  const { session, ready, email, signOut } = useAuthSession();
  const hydrated = useHydrated();
  const you = campIdentityFor(
    hydrated ? getSessionPeerId() : "",
    useAvatarChoice(),
  );
  const name = useDisplayName() || t("avatar.namePlaceholder");
  const signedIn = Boolean(session);

  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [section, setSection] = useState<SettingsSection>("application");
  const openSection = (next: SettingsSection) => {
    setSection(next);
    setSettingsOpen(true);
  };

  return (
    <>
      {/* Not modal: the menu hands over to a dialog, and a modal menu still
          closing would hold the page's pointer events from under it. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="home-you"
            aria-label={`${t("account.menu")}: ${name}`}
            className="flex min-h-11 items-center gap-2 rounded-full border border-border/30 bg-card/40 py-1 pl-1 pr-2 text-sm font-medium backdrop-blur-sm transition-colors duration-(--motion-base) hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:pr-3"
          >
            <CampAvatar beast={you.animal} size={32} />
            <span className="hidden max-w-32 truncate sm:inline">{name}</span>
            <ChevronDown className={icon} aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          data-testid="account-menu"
          className="w-64"
        >
          <MenuHead name={name} email={signedIn ? email : null} />
          <DropdownMenuSeparator />
          {signedIn && (
            <DropdownMenuItem
              data-testid="account-menu-profile"
              onSelect={() => setProfileOpen(true)}
            >
              <UserRound className={icon} aria-hidden="true" />
              {t("settings.profile")}
            </DropdownMenuItem>
          )}
          <SettingsSub onOpen={openSection} />
          <ThemeSub />
          <LanguageSub />
          <AccountFoot
            ready={ready}
            signedIn={signedIn}
            intent={intent}
            onSignOut={() => void signOut()}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      <HomeProfileDialog
        open={profileOpen && signedIn}
        onOpenChange={setProfileOpen}
        name={name}
        animal={you.animal}
        email={email}
        // Read only while open: the workbench's session is a bare stand-in.
        since={profileOpen && session ? session.user.created_at : null}
      />
      <SettingsButton
        {...devices}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        section={section}
        hideTrigger
      />
    </>
  );
}
